import { BUSINESS_META, type ConversationDetailDTO, type ConversationStatus, type Priority } from '@sac/contracts'
import { CustomerAvatar } from '../CustomerAvatar'
import { ChannelMark, Icon, channelName } from '../icons'
import { Menu } from '../ui/Menu'
import { useUi } from '../../state/UiContext'

const STATUS_OPTIONS: [ConversationStatus, string, string][] = [
  ['new', 'Novo', '#5b6ef5'],
  ['open', 'Em atendimento', '#12a150'],
  ['waiting_customer', 'Aguardando cliente', '#e8a33d'],
  ['waiting_internal', 'Aguardando interno', '#d97706'],
  ['resolved', 'Resolvido', '#8b919d'],
]
const STATUS_META = Object.fromEntries(STATUS_OPTIONS.map(([k, l, c]) => [k, { l, c }])) as Record<
  ConversationStatus,
  { l: string; c: string }
>

export function ConversationHeader({
  conv,
  agents,
  onAssign,
  onStatus,
  onTogglePriority,
  onResolve,
  onRelink,
  onMarkUnread,
}: {
  conv: ConversationDetailDTO
  agents: { id: string; name: string; avatarColor: string }[]
  onAssign: (agentId: string | null) => void
  onStatus: (status: ConversationStatus) => void
  onTogglePriority: () => void
  onResolve: () => void
  onRelink: () => void
  onMarkUnread: () => void
}) {
  const { dispatch } = useUi()
  const st = STATUS_META[conv.status]
  return (
    <div className="conv-head">
      <CustomerAvatar seed={conv.customerId} color={conv.customerColor} name={conv.customerName} size={36} />
      <div className="ch-id">
        <div className="ch-name">
          {conv.customerName}
          {conv.priority === 'high' && (
            <span className="tag" style={{ background: '#fef2f2', color: '#b42318' }}>
              Prioridade alta
            </span>
          )}
        </div>
        <div className="ch-sub">
          <ChannelMark channel={conv.channelType} size={13} business={conv.business} />{' '}
          {channelName(conv.channelType)} ·{' '}
          {/* The LEGAL ENTITY, not the storefront. "Shopee — Kids Decor" sits on the Quartinhos
              store but invoices under Kids, so showing the store read as "Shopee · Quartinhos"
              and contradicted the blue ring the mark was already drawing beside it. Falls back
              to the store only when no business is set. */}
          {conv.business ? BUSINESS_META[conv.business].label : conv.storeName}
          {conv.subjectKind ? (
            <>
              {' · '}
              <span style={{ color: 'var(--text-2)' }}>{conv.subjectKind}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="ch-ctrl">
        {/* Below 1120px the customer panel is a drawer, and nothing opened it — the CSS for this
            button existed but no component rendered it, so Cliente/Produtos/Tickets were simply
            unreachable on a phone or tablet. */}
        <button
          className="icon-btn ctx-toggle-btn"
          onClick={() => dispatch({ type: 'toggleCtx' })}
          title="Cliente, pedidos e tickets"
          aria-label="Cliente, pedidos e tickets"
        >
          <Icon name="user" />
        </button>
        {/* Empilhados, um sobre o outro: lado a lado eles tomavam a largura que o nome precisa,
            e o nome, espremido, arrastava o resto do cabeçalho para baixo. */}
        <div className="ch-stack">
        <Menu
          trigger={(open) => (
            <button className="sel" onClick={open}>
              <span className="k">Responsável:</span>
              <span className="v">{conv.assignedAgentName ?? 'Não atribuído'}</span>
              <Icon name="chev" size={12} />
            </button>
          )}
        >
          {(close) => (
            <>
              {agents.map((a) => (
                <button
                  key={a.id}
                  className={`pop-item ${conv.assignedAgentId === a.id ? 'on' : ''}`}
                  onClick={() => {
                    onAssign(a.id)
                    close()
                  }}
                >
                  <span className="avatar" style={{ width: 22, height: 22, fontSize: 9, background: a.avatarColor }}>
                    {a.name.slice(0, 2).toUpperCase()}
                  </span>
                  {a.name}
                  {conv.assignedAgentId === a.id && (
                    <span className="ck">
                      <Icon name="ck" />
                    </span>
                  )}
                </button>
              ))}
              <div className="pop-sep" />
              <button
                className={`pop-item ${!conv.assignedAgentId ? 'on' : ''}`}
                onClick={() => {
                  onAssign(null)
                  close()
                }}
              >
                Não atribuído
              </button>
            </>
          )}
        </Menu>

        <Menu
          trigger={(open) => (
            <button className="sel" onClick={open}>
              <span className="stat-dot" style={{ background: st.c }} />
              <span className="v">{st.l}</span>
              <Icon name="chev" size={12} />
            </button>
          )}
        >
          {(close) => (
            <>
              {STATUS_OPTIONS.map(([k, l, c]) => (
                <button
                  key={k}
                  className={`pop-item ${conv.status === k ? 'on' : ''}`}
                  onClick={() => {
                    onStatus(k)
                    close()
                  }}
                >
                  <span className="stat-dot" style={{ background: c }} />
                  {l}
                  {conv.status === k && (
                    <span className="ck">
                      <Icon name="ck" />
                    </span>
                  )}
                </button>
              ))}
            </>
          )}
        </Menu>

        </div>
        <Menu
          align="right"
          trigger={(open) => (
            <button className="icon-btn" onClick={open}>
              <Icon name="dots" />
            </button>
          )}
        >
          {(close) => (
            <>
              <button
                className="pop-item"
                onClick={() => {
                  onTogglePriority()
                  close()
                }}
              >
                Marcar prioridade
              </button>
              <button
                className="pop-item"
                onClick={() => {
                  onRelink()
                  close()
                }}
              >
                Vincular cliente
              </button>
              {/* Cleared the next time the conversation is opened, not on the click that sets
                  it — otherwise flagging one while reading it would undo itself immediately. */}
              <button
                className="pop-item"
                onClick={() => {
                  onMarkUnread()
                  close()
                }}
              >
                Marcar como não lido
              </button>
              <div className="pop-sep" />
              <button
                className="pop-item"
                onClick={() => {
                  onResolve()
                  close()
                }}
              >
                Fechar conversa
              </button>
            </>
          )}
        </Menu>
      </div>
      {/* Faixa própria, na largura inteira do cabeçalho. Espremido na coluna do nome, o UUID
          quebrava em cinco linhas; aqui cabe numa só e ainda sobra. */}
      <button className="ch-convid" title="Clique para copiar" onClick={() => void navigator.clipboard.writeText(conv.id)}>
        <Icon name="copy" size={11} />
        ID da conversa: {conv.id}
      </button>
    </div>
  )
}
